import { AIRY_PRODUCT_CARD } from '@/previewThemes/imageSlots';
import type { ThemeMeta, ThemeTokens } from '@/previewThemes/meta';

/**
 * Serra's design constants.
 *
 * A luxury house: a centred serif masthead, hairline rules and a portrait
 * gallery. The shop it is previewed with is a hair and beauty salon - see
 * `catalog.ts` for why it ships its own rather than naming an industry one,
 * `photos.ts` for the work it is photographed with, and `appointments.ts` for
 * the calendar that stands in place of a basket.
 *
 * Nothing below changed when the sample shop did. That is the point of the
 * split: a theme is a look, and this look was always a page that is mostly
 * photograph with a small amount of type on it - which is a boutique and a
 * salon in equal measure.
 */
export const tokens: ThemeTokens = {
  headingFont: 'Instrument Serif',
  // The house bronze. Everything structural in this theme is near-black on
  // greige; this is the one warm note, and it is what makes the look read as
  // a fashion house rather than a monochrome template.
  accent: '#8a6a3a',
  monoFont: null,
  headingTracking: '-0.01em',
  headingTransform: 'none',
  // 1320px - a fixed editorial measure, wider than editorial's 80rem.
  containerMax: '82.5rem',
  spaceScale: 1.3,
  // The announcement rail plus the masthead row beneath it.
  headerHeight: '7.5rem',
};

export const meta: ThemeMeta = {
  label: 'Serra',
  hint: 'A house that takes appointments: serif masthead, hairline rules and a booking calendar.',
  // Beauty first, because that is the shop this theme is previewed as and the
  // one its sample content is written for. The other two stay: the layout is
  // unchanged and a boutique choosing on the strength of the masthead is
  // choosing correctly - they will simply see a salon in the preview.
  industries: ['beauty_cosmetics', 'salon_beauty_services', 'fashion_apparel', 'jewellery'],
  look: {
    // Greige and near-black, with the bronze accent living in `tokens` -
    // it belongs to the theme's drawing rather than to a merchant's palette.
    colors: {
      primary: '#161412',
      background: '#faf8f5',
      surface: '#efebe4',
      text: '#161412',
      muted: '#8a8378',
    },
    typography: {
      font_family: 'Figtree',
      heading_font: 'Instrument Serif',
      heading_weight: '500',
    },
    buttons: { radius_px: 0, style: 'outline' },
    // No `catalog` named: this theme ships its own in `catalog.ts`. A salon's
    // price list is a shop of services with a photograph each and no basket
    // behind them, and none of the six industry shops has that shape.
  },
  // `listing.card` and `search.card` are `airy` in `theme.tsx`, which
  // ProductCard draws `aspect-[3/4]`; the product page's gallery is `portrait`
  // (4:5). See `imageSlots.ts`.
  imageSpecs: { PRODUCT: AIRY_PRODUCT_CARD },
};
